import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { LogOut, Moon, Sun, Plus, Settings } from 'lucide-react';
import NotesWidget from '../components/widgets/NotesWidget';
import RemindersWidget from '../components/widgets/RemindersWidget';
import CalendarWidget from '../components/widgets/CalendarWidget';
import GalleryWidget from '../components/widgets/GalleryWidget';
import MailWidget from '../components/widgets/MailWidget';
import DriveWidget from '../components/widgets/DriveWidget';
import AppsWidget from '../components/widgets/AppsWidget';

const Dashboard = () => {
  const { user, profile, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [widgets] = useState(['apps', 'gallery', 'notes', 'reminders', 'calendar', 'mail', 'drive']);

  const handleLogout = () => {
    logout();
  };

  const renderWidget = (widgetType) => {
    switch (widgetType) {
      case 'apps':
        return <AppsWidget key="apps" />;
      case 'gallery':
        return <GalleryWidget key="gallery" />;
      case 'notes':
        return <NotesWidget key="notes" />;
      case 'reminders':
        return <RemindersWidget key="reminders" />;
      case 'calendar':
        return <CalendarWidget key="calendar" />;
      case 'mail':
        return <MailWidget key="mail" />;
      case 'drive':
        return <DriveWidget key="drive" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fafafa] via-[#f0f4ff] to-[#fdf4ff] dark:bg-none dark:bg-[#0f1117] transition-colors duration-300">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl dark:bg-[#0f1117] border-b border-indigo-50 dark:border-[#1a2035] sticky top-0 z-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-[#1a1625] dark:text-[#e5e5e5]">Ventes Suite</h1>
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-[#6b7280] hover:text-[#1a1625] hover:bg-indigo-50/50 dark:text-[#888] dark:hover:text-[#e5e5e5] dark:hover:bg-[#1a1a20]"
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-[#6b7280] hover:text-[#1a1625] hover:bg-indigo-50/50 dark:text-[#888] dark:hover:text-[#e5e5e5] dark:hover:bg-[#1a1a20]"
              >
                <Plus size={20} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-[#6b7280] hover:text-[#1a1625] hover:bg-indigo-50/50 dark:text-[#888] dark:hover:text-[#e5e5e5] dark:hover:bg-[#1a1a20]"
              >
                <Settings size={20} />
              </Button>
              <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-indigo-100 dark:border-[#1e2433]">
                <Link to="/profile" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                  <div className="text-right">
                    <p className="text-sm font-medium text-[#1a1625] dark:text-[#e5e5e5]">{profile?.name || user?.email}</p>
                    <p className="text-xs text-[#6b7280] dark:text-[#888]">{user?.email}</p>
                  </div>
                  <Avatar>
                    <AvatarImage src={profile?.avatar_url} alt={profile?.name} />
                    <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                      {(profile?.name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="text-[#6b7280] hover:bg-red-50 hover:text-red-500 dark:text-[#888] dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <LogOut size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Grid */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {widgets.map((widget) => renderWidget(widget))}
        </div>
      </main>

      {/* Storage Info */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#161b27] dark:border-[#1e2433] dark:shadow-none rounded-2xl p-6 transition-colors duration-300">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#1a1625] dark:text-[#e5e5e5] mb-2">Storage</h3>
              <p className="text-sm text-[#6b7280] dark:text-[#888]">
                28.5 GB of 50 GB used
              </p>
            </div>
            <div className="flex-1 max-w-md ml-8">
              <div className="h-2 bg-indigo-50 dark:bg-[#1a1a20] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                  style={{ width: '57%' }}
                />
              </div>
            </div>
            <Button className="ml-8 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0 shadow-md">
              Upgrade
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
